import { useState, useEffect } from "react";
import { get2FAStatus, setup2FA, enable2FA, disable2FA } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, ShieldCheck, ShieldOff, Loader2, Copy, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function TwoFactorSetup({ trigger }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState("status"); // status, setup, backup, verify, disable

  useEffect(() => {
    if (open) {
      loadStatus();
    }
  }, [open]);

  const loadStatus = async () => {
    try {
      const data = await get2FAStatus();
      setStatus(data);
      setStep(data.is_enabled ? "status" : "status");
    } catch (error) {
      toast.error("Failed to load 2FA status");
    }
  };

  const handleSetup = async () => {
    setIsLoading(true);
    try {
      const data = await setup2FA();
      setSetupData(data);
      setStep("backup");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to setup 2FA");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnable = async () => {
    if (code.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setIsLoading(true);
    try {
      await enable2FA(code);
      toast.success("Two-factor authentication enabled!");
      setCode("");
      setSetupData(null);
      setStep("status");
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setIsLoading(true);
    try {
      await disable2FA(disableCode);
      toast.success("Two-factor authentication disabled");
      setDisableCode("");
      setStep("status");
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Shield className="w-4 h-4 mr-2" />
            2FA Settings
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="2fa-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            Add an extra layer of security to your account
          </DialogDescription>
        </DialogHeader>

        {/* Status View */}
        {step === "status" && status && (
          <div className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">2FA Status</CardTitle>
                  <Badge variant={status.is_enabled ? "default" : "secondary"}>
                    {status.is_enabled ? (
                      <>
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        Enabled
                      </>
                    ) : (
                      <>
                        <ShieldOff className="w-3 h-3 mr-1" />
                        Disabled
                      </>
                    )}
                  </Badge>
                </div>
                <CardDescription>
                  {status.is_enabled 
                    ? "Your account is protected with two-factor authentication"
                    : "Enable 2FA to secure your account with an authenticator app"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {status.is_enabled ? (
                  <div className="space-y-4">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-green-800">Account Protected</p>
                          <p className="text-xs text-green-600">
                            {status.last_used && `Last used: ${new Date(status.last_used).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="destructive" 
                      className="w-full"
                      onClick={() => setStep("disable")}
                      data-testid="disable-2fa-btn"
                    >
                      <ShieldOff className="w-4 h-4 mr-2" />
                      Disable 2FA
                    </Button>
                  </div>
                ) : (
                  <Button 
                    className="w-full"
                    onClick={handleSetup}
                    disabled={isLoading}
                    data-testid="setup-2fa-btn"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 mr-2" />
                    )}
                    Enable 2FA
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Backup Codes View */}
        {step === "backup" && setupData && (
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Save Your Backup Codes</p>
                  <p className="text-xs text-amber-600">
                    Store these codes securely. You can use them if you lose access to your authenticator app.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {setupData.backup_codes.map((code, i) => (
                <div 
                  key={i}
                  className="p-2 bg-muted rounded font-mono text-sm text-center cursor-pointer hover:bg-muted/80"
                  onClick={() => copyToClipboard(code)}
                >
                  {code}
                </div>
              ))}
            </div>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => copyToClipboard(setupData.backup_codes.join('\n'))}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy All Codes
            </Button>
            
            <Button 
              className="w-full"
              onClick={() => setStep("setup")}
              data-testid="continue-to-qr-btn"
            >
              {"I've"} Saved My Codes - Continue
            </Button>
          </div>
        )}

        {/* QR Code Setup View */}
        {step === "setup" && setupData && (
          <div className="space-y-4 mt-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              <div className="inline-block p-4 bg-white rounded-lg border" data-testid="qr-code-container">
                <img 
                  src={setupData.qr_code} 
                  alt="2FA QR Code" 
                  className="w-48 h-48 mx-auto"
                />
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Or enter this code manually:</p>
              <div 
                className="font-mono text-sm bg-muted p-2 rounded cursor-pointer hover:bg-muted/80"
                onClick={() => copyToClipboard(setupData.secret)}
              >
                {setupData.secret}
                <Copy className="w-3 h-3 inline ml-2" />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="verify-code">Enter the 6-digit code from your app</Label>
              <Input
                id="verify-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
                data-testid="2fa-verify-input"
              />
            </div>
            
            <Button 
              className="w-full"
              onClick={handleEnable}
              disabled={isLoading || code.length !== 6}
              data-testid="verify-2fa-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Verify and Enable
            </Button>
          </div>
        )}

        {/* Disable View */}
        {step === "disable" && (
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Warning</p>
                  <p className="text-xs text-red-600">
                    Disabling 2FA will make your account less secure. Enter your current authenticator code to confirm.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="disable-code">Enter your current 6-digit code</Label>
              <Input
                id="disable-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
                data-testid="disable-2fa-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setStep("status");
                  setDisableCode("");
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                className="flex-1"
                onClick={handleDisable}
                disabled={isLoading || disableCode.length !== 6}
                data-testid="confirm-disable-2fa-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShieldOff className="w-4 h-4 mr-2" />
                )}
                Disable 2FA
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
